import { Badge, Grid } from '@amzn/awsui-components-react-v3';
import { OptionDefinition } from '@amzn/awsui-components-react-v3/polaris/internal/components/option/interfaces';
import React from 'react';
import { logger } from '@amzn/awsui-components-react/polaris/internal/core/runtime/src';
import {
  CmsContainer,
  ContainerDefinition,
  GetContainerResponse,
  Policy,
} from '../../../services/container-management/types';
import { ContainerRule, ContainerBlock, PolicyAction, PolicyEffect } from '../types';

class PolicyActionNotRecognizedError extends Error {
  constructor(msg: string) {
    super(msg);
    Object.setPrototypeOf(this, PolicyActionNotRecognizedError.prototype);
  }
}

const RESOURCE_ARN_PREFIX = 'arn:scm:cms:::';

// container definition arn's are in the format of type/category/subcategory
const TRIPLE_SLASH_REGEX = '^.+/.+/.+$';

const isResourceArnFromContainerDefinition = (resourceArn: string): boolean => {
  const arn = resourceArn.toLowerCase();
  if (!arn.startsWith(RESOURCE_ARN_PREFIX)) {
    logger.warn(`unrecognized arn prefix for arn: ${arn}`);
    return false;
  }
  const suffix = arn.substring(RESOURCE_ARN_PREFIX.length);
  if (suffix.match(TRIPLE_SLASH_REGEX)) {
    return true;
  }
  return false;
};

const transformPolicyActionToContainerBlock = (action: string): ContainerBlock => {
  switch (action) {
    case PolicyAction.CycleCount:
      return ContainerBlock.CycleCountBlock;
    case PolicyAction.Stow:
      return ContainerBlock.StowBlock;
    case PolicyAction.Pick:
      return ContainerBlock.PickBlock;
    default:
      throw new PolicyActionNotRecognizedError(
        `Policy Action not recognized. Skipping action: ${action}`
      );
  }
};

export const transformPoliciesToRules = (
  policies: Policy[]
): Map<ContainerBlock, ContainerRule> => {
  const rules = new Map<ContainerBlock, ContainerRule>();

  // every action is implicitly denied until a policy refers to it
  const implicitDenies = new Set(Object.keys(PolicyAction));

  policies.forEach((policy) => {
    policy.statements.forEach((statement) => {
      if (!statement || !statement.action || !statement.effect || !statement.resource) {
        logger.warn(`Policy statement is not complete: ${statement}`);
        return;
      }

      const isOverride = !isResourceArnFromContainerDefinition(statement.resource);
      const enabled = statement.effect.toUpperCase() === PolicyEffect.Deny.toUpperCase();

      try {
        const block = transformPolicyActionToContainerBlock(statement.action);

        if (rules.has(block)) {
          const existingBlock = rules.get(block);
          if (!existingBlock?.enabled) {
            // replace the block if new block is enabled or if existing block is an override (for consistency)
            if (enabled || existingBlock?.isOverride) {
              rules.set(block, {
                rule: block,
                enabled,
                isOverride,
                policyId: policy.policyId,
              });
            }
          } else if (enabled && existingBlock?.isOverride) {
            // replace the block if the new block is also enabled and the existing block is an override (for consistency)
            rules.set(block, {
              rule: block,
              enabled,
              isOverride,
              policyId: policy.policyId,
            });
          }
        } else {
          rules.set(block, {
            rule: block,
            enabled,
            isOverride,
            policyId: policy.policyId,
          });
        }

        // whether the rule is allowed or denied, it is no longer implicitly denied
        implicitDenies.delete(statement.action);
      } catch (e) {
        logger.warn(e);
      }
    });
  });

  // add implicitly denied actions to the rules list as enabled blocks
  implicitDenies.forEach((action) => {
    const block = transformPolicyActionToContainerBlock(action);
    rules.set(block, {
      rule: block,
      enabled: true,
      isOverride: false,
      policyId: null,
    });
  });

  return rules;
};

export const transformContainerForView = (getContainerResponse: GetContainerResponse): any => {
  return {
    getContainerResponse,
    rules: transformPoliciesToRules(getContainerResponse.policies),
  };
};

export const getContainerBlock = (s: string | undefined): ContainerBlock | null => {
  let ret = null;
  Object.values(ContainerBlock).forEach((block) => {
    if (block === s) {
      ret = block;
    }
  });
  return ret;
};

export const getSizeOption = (
  sizeOptions: OptionDefinition[],
  size: string
): OptionDefinition | null => {
  let result = null;
  sizeOptions.forEach((option) => {
    if (option.value === size) {
      result = option;
    }
  });
  return result;
};

export const getRulesList = (rules: ContainerRule[]): OptionDefinition[] => {
  return rules
    .filter((cmsRule) => cmsRule.enabled)
    .map((cmsRule) => {
      return {
        label: cmsRule.rule,
        value: cmsRule.rule,
      };
    });
};

export const getRulesOptions = (rules: ContainerRule[]): OptionDefinition[] =>
  rules
    .filter((cmsRule) => !cmsRule.enabled)
    .map((cmsRule) => {
      return {
        label: cmsRule.rule,
        value: cmsRule.rule,
      };
    });

export const transformContainerBlockToPolicyAction = (block: string): PolicyAction => {
  switch (block) {
    case ContainerBlock.CycleCountBlock:
      return PolicyAction.CycleCount;
    case ContainerBlock.StowBlock:
      return PolicyAction.Stow;
    case ContainerBlock.PickBlock:
      return PolicyAction.Pick;
    default:
      throw new Error(`ContainerBlock not recognized: ${block}`);
  }
};

export const getContainerArn = (containerId: string): string => {
  return `arn:scm:cms:::${containerId}`;
};

// Render an array of ContainerRule into UI badges.
// TODO unify this with the code used in container detail?
export const renderRules = (rules: ContainerRule[]) => {
  const badges = rules
    .filter((rule) => rule.enabled || rule.isOverride)
    .map((rule) => {
      // eslint-disable-next-line no-nested-ternary
      const color = rule.isOverride ? (rule.enabled ? 'green' : 'red') : 'grey';
      return (
        <Badge color={color} key={rule.rule}>
          {rule.rule}
        </Badge>
      );
    });
  return badges.length > 0 ? <Grid>{badges}</Grid> : 'N/A';
};

export const isContainerDefinitionMovable = (containerDefinition: ContainerDefinition): boolean =>
  Boolean(
    containerDefinition?.metadataConfiguration?.moveSupport?.supportedParentLocations?.length
  );

export const getFQContainerName = (
  container: CmsContainer,
  containerDefinition: ContainerDefinition,
  site: string,
  room: string
): string => {
  if (isContainerDefinitionMovable(containerDefinition)) {
    return container.containerName.replace(/\s+/g, '');
  }
  return `${site ? `${site}.` : ''}
    ${room ? `${room}.` : ''}
    ${container.metadata.alphabetIdentifier ? `${container.metadata.alphabetIdentifier}` : ''}
    ${container.metadata.section ? `${container.metadata.section}` : ''}
    ${container.metadata.aisle ? `${container.metadata.aisle}` : ''}
    ${container.metadata.level ? `${container.metadata.level}` : ''}
    ${container.containerName}`.replace(/\s+/g, '');
};
